import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { useState, type ReactNode } from 'react'
import { LockedPadlockOMIcon, UnlockedPadlockOMIcon } from '@omnea-app/icons'
import { ConfirmDialog } from '@omnea-app/omui/ConfirmDialog'
import { recordAuthorization, type RecordVerb } from './accessRules'
import { type InheritedParent } from './inheritedConfidentiality'
import { noRole } from './roleAuthorization'
import { useInheritedConfidentialityCopy } from './useInheritedConfidentialityCopy'
import { useIsPermitted } from './useIsPermitted'
import { useUpdateRecordConfidentiality } from './useUpdateRecordConfidentiality'
import type { ClearanceEntity } from '@omnea-digital/authz-model'
import type { QueryKey } from '@tanstack/react-query'

type ConfidentialityKey =
  | 'confirm-title'
  | 'confirm-description'
  | 'confirm-self-lockout-warning'
  | 'confirm-mark'
  | 'confirm-cancel'
  | 'unmark-confirm-title'
  | 'unmark-confirm-description'
  | 'unmark-confirm-remove'
  | 'update-failed'

type Props<Entity extends ClearanceEntity> = {
  entity: Entity
  recordId: string
  isConfidential: boolean
  inheritedFrom: readonly InheritedParent[]
  permission: RecordVerb<Entity>
  invalidateKeys: QueryKey[]
  translateCopy: (key: ConfidentialityKey) => string
  labels: { mark: string; unmark: string }
  /** Called when the confirmation closes, never on click: a menu that unmounts its items on close would take the open dialog with it. */
  onClose?: () => void
  /** Replaces the mark confirmation, for an entity whose marking does more than mark this record. It does the marking. */
  renderMarkDialog?: (close: () => void, bodyTexts: string[]) => ReactNode
}

export const ConfidentialRecordMenuItem = <Entity extends ClearanceEntity>({
  entity,
  recordId,
  isConfidential,
  inheritedFrom,
  permission,
  invalidateKeys,
  translateCopy: t,
  labels,
  onClose,
  renderMarkDialog,
}: Props<Entity>) => {
  const isPermittedToMark = useIsPermitted({ authorization: recordAuthorization(entity, permission, noRole) }, recordId)
  const { mutate, isPending } = useUpdateRecordConfidentiality({
    entity,
    recordId,
    invalidateKeys,
    errorMessage: t('update-failed'),
  })
  // Fixed when the dialog opens: a refetch or a poll can return the new mark while it is still open, and swapping it for
  // the unmark confirmation then would offer to undo the mark just made.
  const [openedFor, setOpenedFor] = useState<'mark' | 'unmark' | null>(null)
  const isUnmarking = openedFor === 'unmark'
  const inherited = useInheritedConfidentialityCopy(inheritedFrom)

  if (!isPermittedToMark) return null

  // With a confidential parent, removing the own mark opens the record to that parent's readers only, not to every
  // broad reader, so the usual warning would be wrong.
  const unmarkDescription = inherited?.unmarkKeepsConfidential ?? t('unmark-confirm-description')
  // An own mark also drops the access people hold through the parent, so the usual restriction text still applies.
  const markDescription = [
    ...(inherited ? [inherited.markKeepsConfidential] : []),
    t('confirm-description'),
    t('confirm-self-lockout-warning'),
  ]

  const closeDialog = () => {
    setOpenedFor(null)
    onClose?.()
  }

  return (
    <>
      {/* Disabled while the flip is in flight, so reopening the menu against the stale flag cannot queue a second submit. */}
      <MenuItem
        onClick={() => setOpenedFor(isConfidential ? 'unmark' : 'mark')}
        disabled={isPending}
        data-testid={`confidential-${entity}-menu-item`}
      >
        <ListItemIcon>{isConfidential ? <UnlockedPadlockOMIcon /> : <LockedPadlockOMIcon />}</ListItemIcon>
        <ListItemText>{isConfidential ? labels.unmark : labels.mark}</ListItemText>
      </MenuItem>
      {renderMarkDialog != null && openedFor === 'mark' ? (
        renderMarkDialog(closeDialog, markDescription)
      ) : (
        <ConfirmDialog
          open={openedFor != null}
          onClose={closeDialog}
          onConfirm={() => mutate(!isUnmarking)}
          title={isUnmarking ? t('unmark-confirm-title') : t('confirm-title')}
          bodyTexts={isUnmarking ? [unmarkDescription] : markDescription}
          actions={[
            { variant: 'contained', color: 'secondary', text: t('confirm-cancel'), action: 'cancel' },
            {
              variant: 'contained',
              color: 'error',
              text: isUnmarking ? t('unmark-confirm-remove') : t('confirm-mark'),
              action: 'confirm',
            },
          ]}
        />
      )}
    </>
  )
}
